import { inject, Injectable } from "@angular/core";
import { combineLatest, EMPTY, Observable, of } from "rxjs";
import { catchError, map, shareReplay, take } from "rxjs/operators";
import { Helper } from "@helpers/functions";
import { ASSET_FAVORITED, ASSET_UNFAVORITED } from "@helpers/constants";
import { Tradable } from "@models/interfaces/tradable";

import { Metal } from "@models/asset/metal/metal";
import { SegmentService } from "@main-services/segment.service";
import { TranslationService } from "@core-services/translation.service";
import { InfoService } from "@core-services/info.service";
import { WatchlistStateModel, WatchlistStateService } from "@modules/watchlist/domain/+state/watchlist.state.service";
import { WatchlistRemote } from "@modules/watchlist/domain/infrastructure/watchlist.remote";
import { AddWatchListModalComponent } from "@modules/main/components/add-watchlist-modal/add-watchlist-modal.component";
import { ModalService } from "@core-services/modal.service";
import { StatusMessages } from "@modules/watchlist/domain/const/const";
import { Dictionary } from "@helpers/types";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { NotificationsFacade } from "@modules/notifications/domain/+state/notifications.facade";
import { NotificationSocketEvent } from "@models/transaction/messaging-socket-events";
import { NotificationTargets } from "@modules/notifications/domain/entities/notifications.model";
import { UserService } from "@main-services/user.service";
import { AccountService } from "@modules/account/services/account.service";
import { WEB3_MERGE_PHASE, Web3Facade } from "@modules/web3/+state/web3.facade";
import { filterTradableUKUsers } from "@modules/trade/helpers/helper";
import { RudderstackService } from "@main-services/rudderstack.service";
import { BFFAssetsService } from "@core-services/bff-assets.service";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { TradablesStateService } from "@modules/+new/core/tradables/domain/+state/tradables.state.service";
import { TradablesBridgeService } from "@modules/+new/core/tradables/domain/infrastructure/tradables.bridge.service";

@Injectable({
	providedIn: "root"
})
export class WatchlistFacade {
	public constructor(
		private infoService: InfoService,
		private translationService: TranslationService,
		private segmentService: SegmentService,
		private rudderstackService: RudderstackService,
		private readonly watchlistStateService: WatchlistStateService,
		private readonly watchlistRemote: WatchlistRemote,
		private readonly modalService: ModalService,
		private readonly BFFAssetsService: BFFAssetsService,
		private readonly tradableListService: TradableListService,
		private readonly tradablesStateService: TradablesStateService,
		private readonly tradablesBridgeService: TradablesBridgeService
	) {}

	private notificationsFacade: NotificationsFacade = inject(NotificationsFacade);
	private launchDarklyService: LaunchDarklyService = inject(LaunchDarklyService);
	private userService: UserService = inject(UserService);
	private accountService: AccountService = inject(AccountService);
	private web3facade: Web3Facade = inject(Web3Facade);

	// we use `shareReplay` in case this is called multiple times, so that the cached version is returned
	private remoteResponse$ = this.watchlistRemote
		.getPaginatedResponse$(1)
		.pipe(shareReplay({ refCount: true, bufferSize: 1 }));

	public getWatchlistState$(): Observable<WatchlistStateModel> {
		// only populate the state via remote once, only if it's not yet populated
		// (or has error - in which case it's also marked as not loaded)
		if (!this.watchlistStateService.isLoadedSuccessfully()) {
			this.fetchWatchlistState();
		}

		// don't emit initial (null) state
		return this.watchlistStateService.getState$();
	}

	public addBffAssetsToWatchlistHelper$<T extends { tradableIds: string[] }>(watchlistModel: T): Observable<T> {
		watchlistModel.tradableIds = watchlistModel.tradableIds.filter((id) =>
			this.tradableListService.getItemById(id)
		);

		return of(watchlistModel);

		//TODO: Re-enable BFF asset fetching in case product decides to use BFF assets in watchlist again
		// if (!missingTradableIds.length) {
		// 	return of(watchlistModel);
		// }

		// const obs$: Observable<Tradable>[] = missingTradableIds.map((id) =>
		// 	this.BFFAssetsService.getAssetFetch({ input: { idInternal: Number(id) } }).pipe(
		// 		map((asset) => new BFFSecurityAsset(asset))
		// 	)
		// );

		// return forkJoin(obs$).pipe(
		// 	map((assets: Tradable[]) => {
		// 		this.tradableListService.addItems(assets);

		// 		//First old tradables need to be converted to new tradables
		// 		const converted = this.tradablesBridgeService.adaptOldTradables(assets);

		// 		//Then new tradables need to be added to the state
		// 		this.tradablesStateService.addTradables(converted);

		// 		return watchlistModel;
		// 	})
		// );
	}

	public getWatchlistDictionary$(): Observable<Dictionary<boolean>> {
		return this.getWatchlistState$().pipe(
			map((state) =>
				state?.tradableIds.reduce((acc, tId) => {
					acc[tId] = state.tradableIds.includes(tId);
					return acc;
				}, {})
			)
		);
	}

	public fetchWatchlistState(): void {
		this.remoteResponse$.subscribe({
			next: (response) => {
				//Filtered tradables for UK users
				const convertToTradable = response?.assets.map((f) => {
					const asset = this.tradableListService.getItemById(f.asset_id);
					return asset;
				});

				const filteredTradablesUKUsers = filterTradableUKUsers(convertToTradable);

				const assetsWithFilteredTradables = response.assets.filter((id) =>
					filteredTradablesUKUsers.some((t) => t.getId() === id.asset_id)
				);

				this.watchlistStateService.updateStateFromApiResponse(assetsWithFilteredTradables);
			},
			error: () => {
				this.watchlistStateService.updateState({
					tradableIds: [],
					watchlistSort: {},
					isSuccess: false,
					isError: true
				});
			}
		});
	}

	public isWatchlistAsset$(id: string): Observable<boolean> {
		return this.getWatchlistState$().pipe(map((state) => state.tradableIds.indexOf(id) > -1));
	}

	// toggle function for convenience - we check if asset is set or not, then call the appropriate endpoint
	public toggleAsset(tradable: Tradable): void {
		combineLatest({
			state: this.watchlistStateService.getState$(),
			mergePhase: this.web3facade.getMergePhase(),
			visionTradable: this.web3facade.getVisionTradable$()
		})
			.pipe(take(1))
			.subscribe(({ state, mergePhase, visionTradable }) => {
				if (mergePhase.phase !== WEB3_MERGE_PHASE.PostMerge && visionTradable?.getPid() === tradable.getPid()) {
					return;
				}
				if (state.tradableIds?.indexOf(tradable.getId()) > -1) {
					this.removeAsset(tradable);
				} else {
					this.addAsset(tradable);
				}
			});
	}

	public addAsset(selected: Tradable): void {
		this.watchlistRemote.addAsset$(selected.getId()).subscribe({
			next: () => {
				// Update flag
				this.watchlistStateService.addAsset(selected.getId());

				// Show success add
				this.handleAddNotification(selected);

				// Segment tracking
				if (selected.getAssetGroupName() === "token") {
					this.rudderstackTracking(ASSET_FAVORITED, selected.getAssetSymbol(), "coin", selected.getId());
				} else {
					this.rudderstackTracking(
						ASSET_FAVORITED,
						selected.getAssetSymbol(),
						selected.getAssetGroupName(),
						selected.getId()
					);
				}
			},
			error: (error) => Helper.handleError(error)
		});
	}

	private handleAddNotification(selected: Tradable): void {
		this.launchDarklyService
			.isActive$(LD_FLAG.LIMIT_ORDERS_BUY)
			.pipe(take(1))
			.subscribe((isActive) => {
				const defaultFiat = this.accountService.getDefaultFiat();
				const event = new NotificationSocketEvent();
				event.pid = selected.getPid();
				event.icon = {
					icon_name: "checkmark",
					icon: "checkmark",
					color: "#00f092",
					color_dark: "#00f092",
					name: "checkmark",
					state: "success"
				};
				const title = this.translationService.translate("watchlist_add_asset_success_message", [
					selected.getName()
				]);
				event.skip_global_notification = false;
				if (
					!isActive ||
					this.userService.isUnverified ||
					!selected.getAssetSettings().isPriceOrderAvailable()
				) {
					event.event = {
						name: "limit_order",
						title,
						showAction: true,
						actionMessage: this.translationService.translate("create_price_alert"),
						target_path: `${
							NotificationTargets.watchlist_add_pa
						}?asset_id=${selected.getId()}&fiat_id=${defaultFiat.getId()}`
					};

					this.notificationsFacade.displayToastMessage(event);
				} else {
					event.event = {
						showAction: true,
						actionMessage: this.translationService.translate("create_buy_order"),
						name: "limit_order",
						title,
						target_path: `${
							NotificationTargets.watchlist_add
						}?asset_id=${selected.getId()}&fiat_id=${defaultFiat.getId()}`
					};

					this.notificationsFacade.displayToastMessage(event);
				}
			});
	}

	public removeAsset(tradable: Tradable): void {
		this.watchlistRemote.removeAsset$(tradable.getId()).subscribe({
			next: () => {
				// Update flag
				this.watchlistStateService.removeAsset(tradable.getId());

				// Show success removal
				this.infoService.setSuccess(
					this.translationService.translate(StatusMessages.RemoveAssetMessage, [
						tradable instanceof Metal ? tradable.getName() : tradable.getSymbol()
					]),
					false,
					1500
				);

				// Segment tracking
				if (tradable.getAssetGroupName() === "token") {
					this.rudderstackTracking(ASSET_UNFAVORITED, tradable.getAssetSymbol(), "coin", tradable.getId());
				} else {
					this.rudderstackTracking(
						ASSET_UNFAVORITED,
						tradable.getAssetSymbol(),
						tradable.getAssetGroupName(),
						tradable.getId()
					);
				}
			},
			error: (error) => {
				Helper.handleError(error);
				return EMPTY;
			}
		});
	}

	public moveAsset(fromIndex: number, toIndex: number, tradableId: string, sortIndex: number): void {
		// first move the asset to visualise it to the user
		this.watchlistStateService.moveAsset(fromIndex, toIndex);

		// then try to update on BE
		this.updateDragDropSortById$(tradableId, sortIndex)
			.pipe(take(1))
			.subscribe((success) => {
				if (!success) {
					// move the asset back if request failed
					this.watchlistStateService.moveAsset(toIndex, fromIndex);
				}
			});
	}

	// Add multiple favourite assets (used by Add Favourites Modal)
	public addMultipleAssets(selected: Tradable[]): void {
		const assetsToAdd = selected.map((item) => item.getId());

		this.watchlistRemote.addMultipleAssets$(assetsToAdd).subscribe({
			next: () => {
				if (selected.length === 1) {
					this.infoService.setSuccess(
						this.translationService.translate(StatusMessages.AddedOne, [
							selected[0] instanceof Metal ? selected[0].getName() : selected[0].getSymbol()
						])
					);
				} else {
					this.infoService.setSuccess(this.translationService.translate(StatusMessages.AddedMultiple));
				}

				// track each added asset
				selected.forEach((t) => {
					if (t.getAssetGroupName() === "token") {
						this.rudderstackTracking(ASSET_FAVORITED, t.getAssetSymbol(), "coin", t.getId());
					} else {
						this.rudderstackTracking(ASSET_FAVORITED, t.getAssetSymbol(), t.getAssetGroupName(), t.getId());
					}
				});

				this.watchlistStateService.addMultipleAssets(selected.map((t) => t.getId()));
			},
			error: (error) => Helper.handleError(error)
		});
	}

	private updateDragDropSortById$(tradableId: string, index: number): Observable<boolean> {
		return this.watchlistRemote.moveAsset$(tradableId, index).pipe(
			map(() => {
				// Show success message
				this.infoService.setSuccess(this.translationService.translate(StatusMessages.DragAndDrop), false, 1500);
				return true;
			}),
			catchError((error: Error) => {
				Helper.handleError(error);
				return of(false);
			})
		);
	}

	public openAddToWatchlistModal(): void {
		this.watchlistStateService
			.getState$()
			.pipe(take(1))
			.subscribe((state) => {
				this.modalService
					.create(AddWatchListModalComponent, { existingAssetIds: state.tradableIds })
					.present()
					.onDismiss.subscribe((result) => {
						// add items from modal to state
						if (result.data?.length > 0) {
							this.addMultipleAssets(result.data);
						}
					});
			});
	}

	private rudderstackTracking(eventName: string, symbol: string, currencyType: string, assetId: string): void {
		this.segmentService.track({
			name: eventName,
			properties: {
				asset: symbol,
				asset_category: currencyType,
				category: "favourites",
				asset_id: assetId
			}
		});
		this.rudderstackService.track({
			name: eventName,
			properties: {
				asset: symbol,
				asset_category: currencyType,
				category: "favourites",
				asset_id: assetId
			}
		});
	}
}
